import React, { useRef, useMemo, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import type {
  EventClickArg,
  EventDropArg,
  DateSelectArg,
  EventHoveringArg,
} from "@fullcalendar/core";
import type { EventResizeDoneArg } from "@fullcalendar/interaction";
import type { CalendarEventItem } from "@/types/task";
import {
  CalendarDays,
  Calendar as CalendarIcon,
  Video,
  Phone,
  Mail,
  MessageSquare,
  CheckSquare,
  Globe,
  Clock,
  User,
  Users,
  MapPin,
  Building2,
  Tag,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { dateLocale, useTimeFormat, withClock } from "@/lib/dateTime";

const HOVER_DELAY_MS = 300;

interface CalendarViewProps {
  events: CalendarEventItem[];
  loading?: boolean;
  onEventClick: (event: CalendarEventItem) => void;
  onSelectSlot: (start: Date, end: Date, allDay: boolean) => void;
  /** `previous` is where it was, so the page can offer Undo */
  onRescheduleEvent: (id: string, start: Date, end: Date | undefined, previous: { start: Date; end?: Date; allDay: boolean }) => Promise<void>;
  onDatesSet?: (start: Date, end: Date, title: string, viewType: string) => void;
  /** The page drives the toolbar through this */
  calendarRef?: React.MutableRefObject<FullCalendar | null>;
  /** Ids of events that overlap another one for the same person */
  clashIds?: Set<string>;
  initialView?: string;
}

export function CalendarView({
  events,
  loading,
  onEventClick,
  onSelectSlot,
  onRescheduleEvent,
  onDatesSet,
  calendarRef: externalRef,
  clashIds,
  initialView = "timeGridWeek",
}: CalendarViewProps) {
  const timeFormat = useTimeFormat();
  const clock = useMemo(
    () => ({ hour: timeFormat === "12h" ? "numeric" : "2-digit", minute: "2-digit", hour12: timeFormat === "12h", meridiem: timeFormat === "12h" ? "short" : false }) as const,
    [timeFormat],
  );
  const ownRef = useRef<FullCalendar | null>(null);
  const calendarRef = externalRef || ownRef;

  // Map CalendarEventItem to FullCalendar event format; colours come from CSS classes (see .crm-cal in index.css)
  const classOf = (item: CalendarEventItem) => {
    if (item.kind === "EXTERNAL") return "ev-external";
    if (item.kind === "DEAL") return "ev-deal";
    if (item.task_status === "DONE") return "ev-done";
    switch (item.task_type) {
      case "MEETING":
        return "ev-meeting";
      case "CALL":
        return "ev-call";
      case "WHATSAPP":
        return "ev-whatsapp";
      default:
        return "ev-task";
    }
  };
  const movable = (item: CalendarEventItem) => item.kind === "TASK" && item.task_status !== "DONE";
  const fcEvents = useMemo(() => {
    return events.map((item) => {
      let start = item.start;
      let end = item.end;
      if (!item.all_day && start) {
        const startTime = new Date(start).getTime();
        const endTime = end ? new Date(end).getTime() : 0;
        const defaultDuration = item.task_type === "MEETING" ? 30 : 15;
        if (!endTime || endTime <= startTime) {
          end = new Date(startTime + defaultDuration * 60 * 1000).toISOString();
        }
      }

      return {
        id: item.id,
        title: item.title,
        start,
        end,
        allDay: item.all_day,
        classNames: [classOf(item), ...(clashIds?.has(item.id) ? ["ev-clash"] : []), ...(movable(item) ? [] : ["ev-locked"])],
        editable: movable(item),
        extendedProps: { rawItem: item },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, clashIds]);

  const [hoveredEvent, setHoveredEvent] = useState<{
    item: CalendarEventItem;
    rect: DOMRect;
  } | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  // The preview waits a moment, so sweeping the pointer across a busy day does not make it flicker
  const handleEventMouseEnter = (arg: EventHoveringArg) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    const raw = arg.event.extendedProps.rawItem as CalendarEventItem;
    if (!raw) return;
    const el = arg.el;
    hoverTimeoutRef.current = setTimeout(() => setHoveredEvent({ item: raw, rect: el.getBoundingClientRect() }), HOVER_DELAY_MS);
  };

  const handleEventMouseLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => setHoveredEvent(null), 120);
  };

  const hideHover = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setHoveredEvent(null);
  };

  const handleEventClick = (arg: EventClickArg) => {
    arg.jsEvent.preventDefault();
    setHoveredEvent(null);
    const raw = arg.event.extendedProps.rawItem as CalendarEventItem;
    if (raw) onEventClick(raw);
  };

  const handleDateSelect = (arg: DateSelectArg) => {
    onSelectSlot(arg.start, arg.end, arg.allDay);
  };

  /** Where the event was before the move, for Undo */
  const before = (arg: EventDropArg | EventResizeDoneArg) => ({ start: arg.oldEvent.start!, end: arg.oldEvent.end || undefined, allDay: arg.oldEvent.allDay });

  const handleMoved = async (arg: EventDropArg | EventResizeDoneArg) => {
    const raw = arg.event.extendedProps.rawItem as CalendarEventItem;
    if (!raw || raw.kind !== "TASK" || raw.task_status === "DONE") {
      arg.revert();
      return;
    }
    try {
      await onRescheduleEvent(raw.id, arg.event.start!, arg.event.allDay ? undefined : arg.event.end || undefined, before(arg));
    } catch {
      arg.revert();
    }
  };
  const handleEventDrop = handleMoved;
  const handleEventResize = handleMoved;

  const renderEventContent = (eventInfo: any) => {
    const raw = eventInfo.event.extendedProps.rawItem as CalendarEventItem;
    if (!raw) return <span>{eventInfo.event.title}</span>;

    const isTimeGrid = eventInfo.view.type.startsWith("timeGrid");
    const isDayView = eventInfo.view.type === "timeGridDay";

    const renderIcon = (size = "size-3") => {
      if (raw.kind === "EXTERNAL") return <Globe className={`${size} shrink-0`} />;
      switch (raw.task_type) {
        case "MEETING":
          return <Video className={`${size} shrink-0`} />;
        case "CALL":
          return <Phone className={`${size} shrink-0`} />;
        case "EMAIL":
          return <Mail className={`${size} shrink-0`} />;
        case "WHATSAPP":
          return <MessageSquare className={`${size} shrink-0`} />;
        default:
          return <CheckSquare className={`${size} shrink-0`} />;
      }
    };

    // Read the times from the event itself so they follow the pointer while it is dragged or resized
    const liveStart = eventInfo.event.start ? eventInfo.event.start.toISOString() : raw.start;
    const liveEnd = eventInfo.event.end ? eventInfo.event.end.toISOString() : raw.end;
    const startTimeStr = !raw.all_day && liveStart
      ? new Date(liveStart).toLocaleTimeString(dateLocale(), withClock({
          hour: "numeric",
          minute: "2-digit" }))
      : "";

    const endTimeStr = !raw.all_day && liveEnd
      ? new Date(liveEnd).toLocaleTimeString(dateLocale(), withClock({
          hour: "numeric",
          minute: "2-digit" }))
      : "";

    const fullTimeStr =
      startTimeStr && endTimeStr ? `${startTimeStr} – ${endTimeStr}` : startTimeStr;

    // Day View: Full width executive agenda card
    if (isDayView) {
      return (
        <div
          className="flex items-center justify-between h-full w-full px-2.5 py-1 text-foreground select-none overflow-hidden"
          title={`${raw.title}${startTimeStr ? ` (${startTimeStr})` : ""}${raw.related_name ? ` • ${raw.related_name}` : ""}`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="p-1 rounded-md bg-foreground/10 shrink-0">
              {renderIcon("size-3.5")}
            </div>
            <div className="min-w-0 flex flex-col justify-center">
              <div className="flex items-center gap-2">
                <span className="font-bold text-xs tracking-tight">
                  {fullTimeStr || startTimeStr}
                </span>
                <span className="text-foreground/50 text-xs">•</span>
                <span className="font-semibold text-xs truncate">
                  {raw.title}
                </span>
                {raw.priority === "HIGH" && (
                  <span className="px-1.5 py-0.2 text-[9px] font-bold rounded bg-danger/80 text-foreground uppercase tracking-wider shrink-0">
                    High
                  </span>
                )}
                {raw.task_status === "DONE" && (
                  <span className="px-1.5 py-0.2 text-[9px] font-bold rounded bg-success/80 text-foreground uppercase tracking-wider shrink-0">
                    Done
                  </span>
                )}
              </div>
              {raw.related_name && (
                <div className="text-[10px] opacity-85 truncate mt-0.5 font-normal flex items-center gap-1.5">
                  <Building2 className="size-3 shrink-0 opacity-70" />
                  <span>{raw.related_name}</span>
                  {raw.location && (
                    <>
                      <span className="opacity-50">•</span>
                      <MapPin className="size-3 shrink-0 opacity-70" />
                      <span>{raw.location}</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          {raw.attendees && raw.attendees.length > 0 && (
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-foreground/10 text-[11px] font-medium shrink-0 ml-2">
              <Users className="size-3 opacity-80" />
              <span>{raw.attendees.length}</span>
            </div>
          )}
        </div>
      );
    }

    // TimeGrid Week View: Smart adaptive card layout
    if (isTimeGrid) {
      const startTimeNum = !raw.all_day && liveStart ? new Date(liveStart).getTime() : 0;
      const endTimeNum = !raw.all_day && liveEnd ? new Date(liveEnd).getTime() : 0;
      const durationMins =
        startTimeNum && endTimeNum
          ? Math.max(1, Math.round((endTimeNum - startTimeNum) / 60000))
          : 15;
      const isVeryShort = durationMins <= 20;

      // Ultra-short events (<= 20 mins): single horizontal row
      if (isVeryShort) {
        return (
          <div
            className="flex items-center gap-1.5 h-full w-full px-1.5 py-0.5 text-foreground select-none overflow-hidden text-left"
            title={`${raw.title}${startTimeStr ? ` (${startTimeStr})` : ""}${raw.related_name ? ` • ${raw.related_name}` : ""}`}
          >
            {renderIcon("size-2.5 shrink-0 opacity-90")}
            <span className="font-bold text-[10px] shrink-0 opacity-95">
              {startTimeStr}
            </span>
            <span className="font-semibold text-[11px] truncate tracking-tight">
              {raw.title}
            </span>
          </div>
        );
      }

      // Standard / Longer events: top time row + title + linked account
      return (
        <div
          className="flex flex-col justify-start h-full w-full px-1.5 py-1 text-foreground select-none overflow-hidden text-left"
          title={`${raw.title}${startTimeStr ? ` (${startTimeStr})` : ""}${raw.related_name ? ` • ${raw.related_name}` : ""}`}
        >
          {/* Top Row: Time + Icon */}
          <div className="flex items-center gap-1 font-semibold text-[10px] leading-tight shrink-0 opacity-95">
            {renderIcon("size-2.5 shrink-0 opacity-90")}
            <span className="truncate">{startTimeStr || eventInfo.timeText}</span>
            {raw.attendees && raw.attendees.length > 0 && (
              <span className="ml-auto text-[8px] font-bold px-1 rounded-full bg-foreground/15 text-foreground shrink-0">
                {raw.attendees.length}
              </span>
            )}
          </div>

          {/* Event Title */}
          <div className="font-semibold text-[11px] leading-snug truncate mt-0.5">
            {raw.title}
          </div>

          {/* Linked Record (Deal, Account, Lead) */}
          {raw.related_name && (
            <div className="text-[9.5px] opacity-85 truncate leading-tight mt-0.5 font-normal">
              {raw.related_name}
            </div>
          )}
        </div>
      );
    }

    // Month / All-day view (compact horizontal bar)
    return (
      <div
        className="flex items-center gap-1.5 px-2 py-1 w-full overflow-hidden leading-tight text-foreground select-none cursor-pointer"
        title={`${raw.title}${startTimeStr ? ` (${startTimeStr})` : ""}${raw.related_name ? ` • ${raw.related_name}` : ""}`}
      >
        {renderIcon("size-3")}
        {startTimeStr && (
          <span className="text-[10px] font-semibold opacity-90 shrink-0">
            {startTimeStr}
          </span>
        )}
        <span className="font-medium truncate text-[11px] tracking-tight">
          {raw.title}
        </span>
        {raw.related_name && (
          <span className="text-[10px] opacity-80 truncate max-w-[85px] shrink-0 font-normal hidden sm:inline">
            • {raw.related_name}
          </span>
        )}
        {raw.attendees && raw.attendees.length > 0 && (
          <span className="ml-auto text-[9px] font-bold px-1.5 py-0.2 rounded-full bg-foreground/10 text-foreground shrink-0">
            {raw.attendees.length}
          </span>
        )}
      </div>
    );
  };

  // Floating Rich Hover Details Card
  const renderHoverTooltip = () => {
    if (!hoveredEvent || typeof document === "undefined") return null;

    const { item, rect } = hoveredEvent;
    const cardWidth = 340;
    const estimatedHeight = 280;
    const padding = 16;

    // Smart horizontal placement:
    // If the hovered element is on the right half of the screen, place card to the LEFT
    // If on the left half, place card to the RIGHT
    const isRightHalf = rect.left > window.innerWidth / 2;
    let left: number;

    if (isRightHalf) {
      left = rect.left - cardWidth - 12;
      if (left < padding) {
        left = rect.right + 12;
      }
    } else {
      left = rect.right + 12;
      if (left + cardWidth > window.innerWidth - padding) {
        left = rect.left - cardWidth - 12;
      }
    }

    // Clamp horizontally within screen bounds
    left = Math.max(padding, Math.min(left, window.innerWidth - cardWidth - padding));

    // Vertical placement: center vertically relative to event
    const eventCenterY = rect.top + rect.height / 2;
    let top = eventCenterY - estimatedHeight / 2;

    // Clamp vertically within screen bounds
    if (top + estimatedHeight > window.innerHeight - padding) {
      top = window.innerHeight - estimatedHeight - padding;
    }
    if (top < padding) {
      top = padding;
    }

    const start = new Date(item.start);
    const end = new Date(item.end);
    const formattedDate = start.toLocaleDateString(dateLocale(), {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const formattedTime = item.all_day
      ? "All Day"
      : `${start.toLocaleTimeString(dateLocale(), withClock({ hour: "numeric", minute: "2-digit" }))} – ${end.toLocaleTimeString(dateLocale(), withClock({ hour: "numeric", minute: "2-digit" }))}`;

    const renderTypeIcon = () => {
      if (item.kind === "EXTERNAL") return <Globe className="size-3.5 text-primary shrink-0" />;
      switch (item.task_type) {
        case "MEETING": return <Video className="size-3.5 text-primary shrink-0" />;
        case "CALL": return <Phone className="size-3.5 text-warning shrink-0" />;
        case "EMAIL": return <Mail className="size-3.5 text-accent-foreground shrink-0" />;
        case "WHATSAPP": return <MessageSquare className="size-3.5 text-success shrink-0" />;
        default: return <CheckSquare className="size-3.5 text-muted-foreground shrink-0" />;
      }
    };

    const typeLabel = item.kind === "EXTERNAL"
      ? (item.provider === "GOOGLE" ? "Google Calendar" : "Outlook Calendar")
      : (item.task_type ? item.task_type.replace("_", " ") : "Task");

    return createPortal(
      <div
        style={{
          position: "fixed",
          left,
          top,
          zIndex: 99999,
          width: cardWidth,
        }}
        className="pointer-events-none bg-card/95 backdrop-blur-md border border-border/80 shadow-2xl rounded-xl p-3.5 text-foreground animate-in fade-in-0 zoom-in-95 duration-150"
      >
        {/* Header: Category & Badges */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-muted text-[11px] font-semibold text-foreground">
            {renderTypeIcon()}
            <span className="capitalize">{typeLabel.toLowerCase()}</span>
          </div>

          <div className="flex items-center gap-1.5">
            {item.priority === "HIGH" && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-danger/15 text-danger">
                HIGH
              </span>
            )}
            {item.task_status === "DONE" ? (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-success/15 text-success flex items-center gap-1">
                <CheckCircle2 className="size-2.5" /> Done
              </span>
            ) : item.task_status === "CANCELLED" ? (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-danger/15 text-danger">
                Cancelled
              </span>
            ) : (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary/15 text-primary">
                Scheduled
              </span>
            )}
          </div>
        </div>

        {/* Full Event Title */}
        <h4 className="font-bold text-sm text-foreground mt-2 leading-snug break-words">
          {item.title}
        </h4>

        {/* Date & Time */}
        <div className="flex items-center gap-1.5 text-muted-foreground mt-1.5 text-[11px]">
          <Clock className="size-3.5 text-primary shrink-0" />
          <span className="font-medium text-foreground">{formattedDate}</span>
          <span>•</span>
          <span>{formattedTime}</span>
        </div>

        {/* Associated CRM Record (Deal, Account, Lead) */}
        {item.related_name && (
          <div className="flex items-center gap-1.5 text-[11px] text-foreground font-medium mt-2 bg-muted/60 px-2.5 py-1.5 rounded-lg border border-border/50">
            <Building2 className="size-3.5 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground text-[10px] uppercase font-bold">
              {item.related_type || "Linked"}:
            </span>
            <span className="truncate font-semibold">{item.related_name}</span>
          </div>
        )}

        {/* Assignee / Owner */}
        {item.assigned_to && (
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-1.5">
            <User className="size-3.5 shrink-0" />
            <span>
              Assigned to:{" "}
              <strong className="text-foreground font-medium">
                {item.assigned_to.first_name} {item.assigned_to.last_name || ""}
              </strong>
            </span>
          </div>
        )}

        {/* Attendees */}
        {item.attendees && item.attendees.length > 0 && (
          <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground mt-1">
            <Users className="size-3.5 mt-0.5 shrink-0" />
            <span className="truncate">
              {item.attendees.length} Attendee{item.attendees.length > 1 ? "s" : ""}:{" "}
              <span className="text-foreground font-medium">
                {item.attendees.map((a) => a.name || a.email).slice(0, 3).join(", ")}
                {item.attendees.length > 3 ? "..." : ""}
              </span>
            </span>
          </div>
        )}

        {/* Location or Online Video Call */}
        {item.online_link ? (
          <div className="flex items-center gap-1.5 text-[11px] text-primary mt-1 font-medium truncate">
            <Video className="size-3.5 shrink-0" />
            <span className="truncate">Video Meeting link included</span>
          </div>
        ) : item.location ? (
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-1 truncate">
            <MapPin className="size-3.5 shrink-0" />
            <span className="truncate">{item.location}</span>
          </div>
        ) : null}

        {/* Description Preview */}
        {item.description && (
          <p className="mt-2 pt-1.5 border-t border-border/60 text-[11px] text-muted-foreground line-clamp-2 leading-relaxed italic">
            "{item.description}"
          </p>
        )}

        {/* Footer Hint */}
        <div className="mt-2.5 pt-1.5 border-t border-border/40 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>
            {item.kind === "TASK" && item.task_status !== "DONE"
              ? "Drag to move · drag an edge to change the length"
              : item.kind === "EXTERNAL"
                ? "Edit this one in Google or Outlook"
                : item.kind === "DEAL"
                  ? "Change the close date on the deal"
                  : "Completed tasks can't be moved"}
          </span>
          <span className="text-primary font-semibold">Open &rarr;</span>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="crm-cal relative w-full h-full bg-card rounded-lg border border-border overflow-hidden">
      {loading && (
        <div className="absolute inset-0 bg-background/50 backdrop-blur-xs z-10 flex items-center justify-center rounded-lg">
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-card border border-border shadow-md text-xs font-semibold">
            <div className="size-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span>Updating calendar schedule...</span>
          </div>
        </div>
      )}

      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={initialView}
        headerToolbar={false}
        dayHeaderFormat={{
          weekday: "short",
          day: "numeric",
          omitCommas: true,
        }}
        eventDisplay="block"
        events={fcEvents}
        editable={true}
        selectable={true}
        selectMirror={true}
        dayMaxEvents={4}
        eventMinHeight={22}
        defaultTimedEventDuration="00:15:00"
        forceEventDuration={true}
        slotDuration="00:30:00"
        snapDuration="00:15:00"
        eventResizableFromStart={true}
        views={{ dayGridMonth: { eventDurationEditable: false } }}
        eventDragStart={hideHover}
        eventResizeStart={hideHover}
        slotLabelInterval="01:00:00"
        slotLabelFormat={clock}
        eventTimeFormat={clock}
        slotEventOverlap={false}
        eventMaxStack={3}
        allDaySlot={true}
        stickyHeaderDates={true}
        moreLinkClick="popover"
        weekends={true}
        firstDay={1}
        nowIndicator={true}
        navLinks={true}
        scrollTime="08:00:00"
        eventClick={handleEventClick}
        eventMouseEnter={handleEventMouseEnter}
        eventMouseLeave={handleEventMouseLeave}
        select={handleDateSelect}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        eventContent={renderEventContent}
        datesSet={(info) => onDatesSet?.(info.start, info.end, info.view.title, info.view.type)}
        height="auto"
        slotMinTime="07:00:00"
        slotMaxTime="21:00:00"
        expandRows={true}
      />

      {renderHoverTooltip()}
    </div>
  );
}
